import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, MethodologySection } from "../lib/api";
import { Download } from "lucide-react";

export function MethodologyPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["methodology"],
    queryFn: () => api.get<{ sections: MethodologySection[]; source_path: string }>(
      "/api/v1/methodology"
    ),
  });

  // Smooth scroll to anchor on hash change
  useEffect(() => {
    if (!data) return;
    const hash = window.location.hash.slice(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [data]);

  if (isLoading) return <div style={{ padding: 32 }}>Завантаження методології…</div>;
  if (error)
    return (
      <div style={{ padding: 32, color: "#c53030" }}>
        Помилка: {(error as any)?.detail || (error as any)?.message}
      </div>
    );
  if (!data) return null;

  return (
    <div style={{ display: "flex", padding: "24px 32px", gap: 32, maxWidth: 1400 }}>
      {/* Sticky TOC */}
      <aside
        style={{
          position: "sticky",
          top: 24,
          alignSelf: "flex-start",
          width: 240,
          flexShrink: 0,
          fontSize: 13,
          color: "#4a5568",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8, color: "#1a365d" }}>Зміст</div>
        {data.sections.map((s) => (
          <a
            key={s.anchor}
            href={`#${s.anchor}`}
            style={{
              display: "block",
              padding: "3px 0",
              paddingLeft: s.level === 3 ? 16 : 0,
              color: "#2c5282",
              textDecoration: "none",
              fontSize: s.level === 3 ? 12.5 : 13,
            }}
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById(s.anchor);
              if (el) el.scrollIntoView({ behavior: "smooth" });
              window.history.replaceState(null, "", `#${s.anchor}`);
            }}
          >
            {s.title.length > 38 ? s.title.slice(0, 38) + "…" : s.title}
          </a>
        ))}
      </aside>

      {/* Body */}
      <article style={{ flex: 1, maxWidth: 800, lineHeight: 1.6, color: "#2d3748" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h1 style={{ fontSize: 26, color: "#1a365d", margin: 0 }}>
            Методологія рейтингу лікарень
          </h1>
          <a
            href="/api/v1/methodology/pdf"
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              background: "#2c5282",
              color: "white",
              borderRadius: 4,
              textDecoration: "none",
              fontSize: 13,
            }}
          >
            <Download size={14} /> PDF
          </a>
        </div>

        {data.sections.map((s) => (
          <section key={s.anchor} id={s.anchor} style={{ marginBottom: 32, scrollMarginTop: 24 }}>
            {s.level === 2 ? (
              <h2 style={{ color: "#2c5282", fontSize: 20, borderBottom: "2px solid #ebf4ff", paddingBottom: 6 }}>
                {s.title}
              </h2>
            ) : (
              <h3 style={{ color: "#2d3748", fontSize: 16, marginTop: 20 }}>
                ▸ {s.title}
              </h3>
            )}
            <div className="md-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{s.body_md}</ReactMarkdown>
            </div>
          </section>
        ))}
      </article>
    </div>
  );
}
